import React from 'react';
import Div from '@/shared/Div';
import Divider from '@/shared/Divider';
import ListMap from '@/components/Map';
import classNames from 'classnames';
import Span from '@/shared/Span';
import Link from 'next/link';

interface Location {
  id: number;
  attributes: {
    name: string;
    address: string | null;
    pincode: string;
    salePhone: string | null;
    servicePhone: string | null;
  };
}

interface LocationsContainerProps {
  visible: boolean;
  locations?: Location[];
  isMobile?: boolean;
  categoryId?: string;
}

const LocationsContainer: React.FC<LocationsContainerProps> = ({
  visible,
  locations = [],
  isMobile,
  categoryId = '',
}) => {
  if (!visible) return null;

  const shouldShowContactInfo = categoryId === 'sales-office';

  if (!locations.length) {
    return (
      <Div
        className={classNames('bg-cs-secondary flex items-center justify-center p-s7', {
          'w-full h-auto': isMobile,
          'w-[820px] min-h-[250px]': !isMobile,
        })}
      >
        <Span className="text-body-lg text-neutral-700">No locations found</Span>
      </Div>
    );
  }

  const locationPairs = !isMobile
    ? locations.reduce((result, item, index) => {
        if (index % 2 === 0) {
          result.push([item, ...(locations[index + 1] ? [locations[index + 1]] : [])]);
        }
        return result;
      }, [] as Location[][])
    : [];

  const renderLocationInfo = (location: Location) => {
    const {
      name = 'Address not available',
      address = '',
      pincode,
      salePhone = 'N/A',
      servicePhone = 'N/A',
    } = location.attributes ?? {};

    return (
      <Div className="flex flex-col items-start gap-s4 flex-1 self-stretch">
        <Div className="text-body-lg text-neutral-900">{name}</Div>
        <Div className="text-body-lg font-bold text-neutral-900">
          {`${address || ''} ${address && pincode ? '-' : ''} ${pincode || ''}`}
        </Div>
        {shouldShowContactInfo && (
          <>
            {salePhone && (
              <Div className="text-body-base text-neutral-700">
                Sales Enquiry :{' '}
                <Link href={`tel:${salePhone}`} className="text-cm-bold">
                  {salePhone}
                </Link>
              </Div>
            )}
            {servicePhone && (
              <Div className="text-body-base text-neutral-700">
                Service Request :{' '}
                <Link href={`tel:${servicePhone}`} className="text-cm-bold">
                  {servicePhone}
                </Link>
              </Div>
            )}
          </>
        )}
      </Div>
    );
  };

  return (
    <Div
      className={classNames('bg-cs-secondary flex flex-col items-end p-s7 gap-s7', {
        'w-full h-auto': isMobile,
        'w-[820px] min-h-[250px]': !isMobile,
      })}
    >
      {isMobile ? (
        <ListMap
          list={locations}
          render={(location, index) => (
            <React.Fragment key={location.id}>
              {renderLocationInfo(location)}
              {index !== locations.length - 1 && <Divider className="w-full my-s4" />}
            </React.Fragment>
          )}
        />
      ) : (
        <ListMap
          list={locationPairs}
          render={(pair, pairIndex) => (
            <React.Fragment key={`pair-${pairIndex}`}>
              <Div className="flex items-start gap-s7 self-stretch w-full">
                <ListMap list={pair} render={(location) => renderLocationInfo(location)} />
              </Div>
              {pairIndex !== locationPairs.length - 1 && <Divider className="w-full my-s4" />}
            </React.Fragment>
          )}
        />
      )}
    </Div>
  );
};

export default LocationsContainer;
